/**
 *  Given a text over image, determine if it is is an image text over image element.
 *  @param {HTMLElement} toi Text over image HTML element
 *  @returns {Boolean} Returns true or false if the text over image element given is an image TOI.
 */
export function isImageTOI(toi) {
    if (!toi) { return false; }
    return !toi.children[0].classList.contains('s-text-over-image-background-color');
}

/**
 *  Given an img element and a sibling toi element, determine if the img has yet to load and attach
 *  an event listener that will adjust the sibling toi height to match the img. If img has been loaded,
 *  just adjust the toi siblings height.
 *  @param {HTMLElement} img HTML img element contained in toi
 *  @param {HTMLElement} toi HTML text over image element
 *  @returns {undefined} Nothing
 */
export function modifySiblingTOIOnLoad(img, toi) {
    if (_.isNil(img[0])) { return; }
    if (!img[0].getAttribute('src')) {
        img.on('load', ({ target }) => {
            const toiOuter = toi.children[0];
            const { height } = target.getBoundingClientRect();
            assignHeightAndDisplay(toiOuter, height);
        });
        return;
    }

    const toiOuter = toi.children[0];
    const { height } = img[0].getBoundingClientRect();
    assignHeightAndDisplay(toiOuter, height);
}

/**
 *  Get the previous and next entries in the module in relation to the toi given.
 *  @param {HTMLElement} toi HTML Text over image element
 *  @returns {Object} Returns an object { previousSibling: ..., nextSibling: ... }.
 *                      If value is false, there was no entry found.
 */
export function getSiblings(toi) {
    const previousEntry = toi.parentElement.previousElementSibling;
    const nextEntry = toi.parentElement.nextElementSibling;

    return {
        previousSibling: !_.isNil(previousEntry) ? previousEntry : false,
        nextSibling: !_.isNil(nextEntry) ? nextEntry : false
    }
}

/**
 *  Determine if toi given has bottom aligned cta buttons
 *  @param {HTMLElement} toi HTML Text over image element
 *  @returns {Number} Returns the height of that unordered list containing the buttons.
 *                      If value is false, there was no entry found.
 */
export function getOuterDivHeightIfBottomCTA(toi) {
    let height = 0;
    if (!toi) { return height; }
    if (toi.className.includes('s-text-over-image__bottom-ctas')) {
        height = toi.querySelector('.c-text-over-image__ul').getBoundingClientRect().height;
    }

    return height;
}

/**
 *  Retrieve the outer and inner div for the toi element given. Choice can be made.
 *  @param {HTMLElement} toi HTML Text over image element
 *  @param {String} choice ['outer', 'inner'] default: ''
 *  @returns {[HTMLElement]} Given a choice, returns the chosen element.
 *                               Default returns array with the outer then inner elements.
 */
export function getOuterInnerDiv(toi, choice = '') {
    if (!toi) {
        if (choice !== '') {
            return false;
        }
        return [false, false];
    }

    const result = [];
    let outer = toi.querySelector('.c-text-over-image__outer');
    let inner = toi.querySelector('.c-text-over-image__inner');

    if (choice === 'outer') {
        result.push(outer);
    } else if (choice === 'inner') {
        result.push(inner);
    } else {
        result.push(outer, inner);
    }

    if (result.length === 1) {
        return result[0];
    }
    return result;
}

/**
 *  Determine the height based of the given toi in regards to the module,
 *  siblings, and mixture of toi types in the parent module.
 *  @param {HTMLElement} toi HTML text over image element being processed.
 *  @returns {Number} Returns the min-height the toi should be.
 */
export function getHeightBasedOnSiblings(toi) {
    let isModifiedWhenImageLoads = false;
    const { previousSibling, nextSibling } = getSiblings(toi);

    // TOI Height Info
    const toiInner = getOuterInnerDiv(toi, 'inner');
    const toiOuterHeight = getOuterDivHeightIfBottomCTA(toi);
    const toiInnerHeight = toiInner.getBoundingClientRect().height;
    const toiHeightSum = toiInnerHeight + toiOuterHeight;

    // Prev/Nex Entry Container height information
    const previousHeight = !!previousSibling ? previousSibling.children[0].getBoundingClientRect().height : 0;
    const nextHeight = !!nextSibling ? nextSibling.children[0].getBoundingClientRect().height : 0;

    let height = Math.max(previousHeight, nextHeight);

    if (height <= 150 || (nextHeight < toiHeightSum || previousHeight < toiHeightSum)) {
        // Is the previous/next entry is an image toi, if true let's wait for it to load.
        if (!!previousSibling && isImageTOI(previousSibling)) {
            isModifiedWhenImageLoads = true;
            const entryImg = $(previousSibling).find('img.lazyload');
            modifySiblingTOIOnLoad(entryImg, toi);
        } else if(!!nextSibling && isImageTOI(nextSibling)) {
            isModifiedWhenImageLoads = true;
            const entryImg = $(nextSibling).find('img.lazyload');
            modifySiblingTOIOnLoad(entryImg, toi);
        } else {
            // Determine if the toi contents (inner & outer divs) have any height information.
            const previousInner = getOuterInnerDiv(previousSibling, 'inner');
            const previousInnerHeight = !!previousInner ? previousInner.getBoundingClientRect().height : 0;

            const nextInner = getOuterInnerDiv(nextSibling, 'inner');
            const nextInnerHeight = !!nextInner ? nextInner.getBoundingClientRect().height : 0;

            const previousOuterHeight = getOuterDivHeightIfBottomCTA(previousSibling);
            const nextOuterHeight = getOuterDivHeightIfBottomCTA(nextSibling);

            height = Math.max((previousInnerHeight + previousOuterHeight), (nextInnerHeight + nextOuterHeight));

            // if the toi contents is still bigger than what we calculate the height to be,
            // then just give it the toi height
            if (height < toiHeightSum && !isModifiedWhenImageLoads) {
                height = toiHeightSum;
            }
        }
    }
    return height;
}

/**
 *  Given a text over image, determine if it is is a background color text over image element.
 *  @param {HTMLElement} toi Text over image HTML element
 *  @returns {Boolean} Returns true or false if the text over image element given is a background color TOI.
 */
export function isUsingBackgroundColor(toi) {
    const toiClassName = toi.classList.value;

    if (_.includes(toiClassName, 's-text-over-image-background-color')) {
        return true;
    } else if (_.includes(toiClassName, 'dom-rr-pdp') || _.includes(toiClassName, 'dom-rr-recentlyViewed')) {
        // These elements have spans that get replaced by DY return true to keep an eye on them.
        return true;
    }
    return false;
}

/**
 *  Called by the Intersection Observer, determine if the current tois are intersecting,
 *  If so, hide them and change their height.
 *  @param {[IntersectionObserverEntry]} tois Array of intersection observer objects.
 *   @returns {undefined} Doesn't return anything.
 */
export function getHeightOfIntersectingTOIS(tois) {
    tois.forEach((toi) => {
        let minHeight = 0;
        if (toi.isIntersecting) {
            if (toi.target.classList.contains('dom-rr-pdp') || toi.target.classList.contains('dom-rr-recentlyViewed')) {
                waitForDYToInjectTOI(toi);
            } else {
                const targetToi = toi.target.children[0];
                targetToi.hidden = true;
                minHeight = getHeightBasedOnSiblings(toi.target);
                assignHeightAndDisplay(targetToi, minHeight);
            }
        }
    });
};

/**
 *  Given a dyTOI element container, wait for it to be populated, then assign the height
 *  @param {HTMLElement} dyTOI HTML dy text over image element.
 *  @returns {undefined} Doesn't return anything.
 */
export function waitForDYToInjectTOI(dyTOI) {
    let intervalRef = setInterval(() => {
        if (dyTOI.target.children[0].classList.contains('s-text-over-image-background-color')) {
            // get the actual text-over-image element from the dom-rr element
            const targetToi = dyTOI.target.children[0].children[0];
            targetToi.hidden = true;
            assignHeightAndDisplay(targetToi, getHeightBasedOnSiblings(dyTOI.target.children[0]));
            clearInterval(intervalRef);
        }
    }, 1000);
}

/**
 *  Assign the min-height to the target text over image element outer div.
 *  @param {HTMLElement} toiOuterDiv text over image outer div child
 *  @param {Number} minHeight Min-height of the text over image
 */
export function assignHeightAndDisplay(toiOuterDiv, minHeight) {
    toiOuterDiv.hidden = false;
    toiOuterDiv.style.minHeight = `${minHeight}px`;
}
